import Vue from 'vue';
import VueRouter, { RouteConfig } from 'vue-router';
import i18n from '@/i18n';
import store from '@/store';

Vue.use(VueRouter);

const routes: Array<RouteConfig> = [
    {
        path: '/',
        name: 'Home',
        redirect: {
            name: store.getters['authenticationStorage/isAuthenticated']
                ? 'Dashboard'
                : 'Login',
        },
        component: () => import('@/views/Layout.vue'),
        children: [
            {
                path: '/dashboard',
                name: 'Dashboard',
                component: () => import('@/views/DashboardView.vue'),
                meta: {
                    title: 'dashboard',
                },
            },
            {
                path: '/admin',
                name: 'Admin',
                component: () => import('@/active-components/admin/Admin.vue'),
                meta: {
                    title: 'admin',
                    onlyAdmin: true,
                },
            },
            {
                path: '/profile',
                name: 'Profile',
                component: () =>
                    import('@/active-components/profile/Profile.vue'),
                meta: {
                    title: 'profile',
                },
            },
            {
                path: '/customer-projects',
                name: 'Customer-Projects',
                component: () =>
                    import(
                        '@/active-components/customer-project/CustomerProjects.vue'
                    ),
                meta: {
                    title: 'customer-projects',
                },
            },
            {
                path: '/customer-projects/:id',
                name: 'Customer-Project',
                component: () =>
                    import(
                        '@/active-components/customer-project/CustomerProject.vue'
                    ),
                meta: {
                    title: 'customer-project',
                },
            },
            {
                path: '/event/:id',
                name: 'Event',
                component: () => import('@/active-components/event/Event.vue'),
                meta: {
                    noHeader: true,
                },
            },
            {
                path: '/sheet/:token',
                name: 'Sheet',
                component: () => import('@/active-components/sheet/Sheet.vue'),
                meta: {
                    unauthenticated: true,
                    noHeader: true,
                },
            },
            {
                path: '/event/:id/editors',
                name: 'EventEditors',
                component: () =>
                    import(
                        '@/active-components/event-editors/EventEditors.vue'
                    ),
            },
            {
                path: '/event/:id/master-data',
                name: 'MasterData',
                component: () =>
                    import('@/active-components/master-data/MasterData.vue'),
                meta: {
                    title: 'master-data',
                    noHeader: true,
                },
            },
            {
                path: '/not-found',
                name: 'NotFound',
                component: () => import('@/views/NotFound.vue'),
                meta: {
                    unauthenticated: true,
                    title: 'not-found',
                },
            },
        ],
    },
    {
        path: '/login',
        name: 'Login',
        component: () => import('@/active-components/login/Login.vue'),
        meta: {
            onlyUnauthenticated: true,
            title: 'login',
        },
    },
    {
        path: '/signup',
        name: 'Signup',
        component: () => import('@/active-components/signup/Signup.vue'),
        meta: {
            onlyUnauthenticated: true,
            title: 'signup',
        },
    },
    {
        path: '/reset-password-request',
        name: 'ResetPasswordRequest',
        component: () =>
            import(
                '@/active-components/reset-password/ResetPasswordRequest.vue'
            ),
        meta: {
            onlyUnauthenticated: true,
            title: 'reset-password',
        },
    },
    {
        path: '/reset-password',
        name: 'ResetPassword',
        component: () =>
            import('@/active-components/reset-password/ResetPassword.vue'),
        meta: {
            onlyUnauthenticated: true,
            title: 'reset-password',
        },
    },
    {
        path: '*',
        redirect: '/not-found',
    },
];

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes,
});

router.beforeEach((to, from, next) => {
    if (to?.matched?.some((record) => record.meta.onlyUnauthenticated)) {
        if (store.getters['authenticationStorage/isAuthenticated']) {
            next({ name: 'Dashboard' });
        } else {
            next();
        }
    } else if (
        to?.matched?.some((record) => record.meta.onlyAdmin) &&
        store.getters['authenticationStorage/isAuthenticated']
    ) {
        if (store.getters['authenticationStorage/getUser']?.role !== 'ADMIN') {
            next({ name: 'Dashboard' });
        } else {
            next();
        }
    } else if (
        store.getters['authenticationStorage/isAuthenticated'] ||
        to?.matched?.some((record) => record.meta.unauthenticated)
    ) {
        next();
    } else {
        next({ name: 'Login' });
    }
});

router.afterEach((to) => {
    Vue.nextTick(() => {
        document.title = i18n
            .t('navigation.title', {
                page: i18n.t(
                    to?.meta?.title
                        ? `navigation.pages.${to.meta.title}`
                        : 'cascade',
                ),
            })
            .toString();
    });
});

export default router;
